import { Field } from '@odin-labs/components';
import React from 'react';

export type NameFieldProps = {
  /** The committed name; edits are held locally until blur or Enter. */
  value: string;
  placeholder?: string;
  error: string | null;
  disabled: boolean;
  /** Resolves false when the layout refused the name; the field then shows the committed name again. */
  onCommit: (name: string) => Promise<boolean> | void;
};

/**
 * Inline name editing for a zone or entry. Typing is local so the caret does
 * not jump while the layout refetches; the value commits on blur or Enter. A
 * name the layout refuses, such as one another zone already has, is dropped
 * for the committed one.
 */
export function NameField({
  value,
  placeholder,
  error,
  disabled,
  onCommit,
}: NameFieldProps): React.ReactElement {
  const [draft, setDraft] = React.useState(value);
  React.useEffect(() => setDraft(value), [value]);

  const commit = async (): Promise<void> => {
    if (draft === value) return;
    const committed = await onCommit(draft);
    if (committed === false) setDraft(value);
  };

  return (
    <Field
      name="name"
      label="Name"
      value={draft}
      placeholder={placeholder}
      disabled={disabled}
      error={error ?? undefined}
      onChange={setDraft}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
      }}
    />
  );
}
